/**
 * Analysis overlay renderer - renders multiple rounds simultaneously.
 * Utility rendering (smokes, fires, projectiles, detonations, trajectories) pulled from replayRenderer.ts.
 * Player rendering is simplified to colored dots (no weapon icons, name labels, HP bars, view cones).
 */

import { ParsedReplay, Frame, RosterEntry, EntityStartEvent, GrenadeDetonateEvent } from '../MatchPage/Replay/replayParser';
import { worldToCanvas, mapConfigs } from '../MatchPage/Replay/replayRenderer';
import { PreloadedIcons } from '../MatchPage/Replay/weaponIcons';

const SMOKE_DURATION_TICKS = 64 * 18;
const FIRE_DURATION_SECONDS = 7;
const SMOKE_RADIUS_WORLD = 150;
const FIRE_RADIUS_WORLD = 105;
const FLAME_RADIUS_WORLD = 48;
const PROJ_TO_WEAPON: Record<number, number> = { 1: 72, 2: 71, 3: 70, 4: 73, 6: 75 };

function worldRadius(r: number, config: { scale: number }, cs: number): number {
    return (r / config.scale) * (cs / 1024);
}

export interface DetonationEvent {
    type: 'flash' | 'he';
    x: number;
    y: number;
    frameIdx: number;
    throwerSteamId: string | null;
    // Thrower's Z at detonation time — used to gate detonations to the correct
    // level on multi-level maps. null when the thrower frame is unavailable.
    throwerZ: number | null;
}

export interface OverlayRoundState {
    replay: ParsedReplay;
    key: string;
    teamSide: 'T' | 'CT';
    smokeTeams: Record<number, 'T' | 'CT'>;
    smokeThrowers: Record<number, string>; // entityId → steamId
    fireTeams: Record<number, 'T' | 'CT'>;
    fireThrowers: Record<number, string>;
    fireStartFrames: Record<number, number>;
    detonations: DetonationEvent[];
    // Projectile path tracking (mutable, updated each render).
    // Each key holds a list of paths so concurrent same-type grenades from one
    // thrower don't get merged into a single zig-zag trajectory.
    projPaths: Map<string, Array<Array<{ x: number; y: number }>>>;
}

export function buildRoundState(replay: ParsedReplay, teamSide: 'T' | 'CT', key: string): OverlayRoundState {
    const smokeTeams: Record<number, 'T' | 'CT'> = {};
    const smokeThrowers: Record<number, string> = {};
    const fireTeams: Record<number, 'T' | 'CT'> = {};
    const fireThrowers: Record<number, string> = {};
    const fireStartFrames: Record<number, number> = {};
    const detonations: DetonationEvent[] = [];

    for (const evt of replay.events) {
        if (evt.type === 'smoke_start') {
            const e = evt as EntityStartEvent;
            const thrower = replay.roster[e.throwerSlot];
            if (thrower) {
                smokeTeams[e.entityId] = thrower.team;
                smokeThrowers[e.entityId] = thrower.steamId;
            }
        } else if (evt.type === 'inferno_start') {
            const e = evt as EntityStartEvent;
            const thrower = replay.roster[e.throwerSlot];
            if (thrower) {
                fireTeams[e.entityId] = thrower.team;
                fireThrowers[e.entityId] = thrower.steamId;
                fireStartFrames[e.entityId] = e.frameIdx;
            }
        } else if (evt.type === 'flash_detonate' || evt.type === 'he_detonate') {
            const e = evt as GrenadeDetonateEvent;
            const thrower = replay.roster[e.throwerSlot];
            let throwerZ: number | null = null;
            if (e.throwerSlot !== 0xff) {
                const frame = replay.frames[Math.min(e.frameIdx, replay.frames.length - 1)];
                if (frame && e.throwerSlot < frame.players.length) {
                    throwerZ = frame.players[e.throwerSlot].z;
                }
            }
            detonations.push({
                type: evt.type === 'flash_detonate' ? 'flash' : 'he',
                x: e.x, y: e.y, frameIdx: e.frameIdx,
                throwerSteamId: thrower?.steamId || null,
                throwerZ,
            });
        }
    }

    return { replay, key, teamSide, smokeTeams, smokeThrowers, fireTeams, fireThrowers, fireStartFrames, detonations, projPaths: new Map() };
}

export interface RenderOptions {
    showTeam: 'team' | 'opponent';
    selectedPlayer: string | null;
    playerColors: string[];
    preloadedIcons: PreloadedIcons | null;
    // When set, this steamId is always drawn with playerColors[0] regardless of roster
    // slot order — so the searched player's dot stays the same color across matches.
    pinnedPlayerSteamId?: string | null;
}

export interface LevelFilter {
    level: 'upper' | 'lower';
    zUpper: number;
    zLower: number;
}

export function renderRoundUtility(
    ctx: CanvasRenderingContext2D,
    frame: Frame,
    rs: OverlayRoundState,
    mapName: string,
    canvasSize: number,
    frameIdx: number,
    options: RenderOptions,
    drawSide: 'T' | 'CT',
    levelFilter?: LevelFilter,
) {
    const config = mapConfigs[mapName];
    if (!config) return;
    const { replay, smokeTeams, smokeThrowers, fireTeams, fireThrowers, fireStartFrames, detonations } = rs;

    // Helper: check if a thrower slot belongs to the side we're drawing
    const isThrowerOnDrawSide = (throwerSlot: number): boolean => {
        if (throwerSlot < 0 || throwerSlot >= replay.roster.length) return false;
        return replay.roster[throwerSlot]?.team === drawSide;
    };

    // --- Smokes ---
    for (const smoke of frame.world.smokes) {
        // Only show utility from the team we're viewing (skip unknown throwers too)
        const throwerTeam = smokeTeams[smoke.entityId];
        if (throwerTeam !== drawSide) continue;
        if (options.selectedPlayer && smokeThrowers[smoke.entityId] !== options.selectedPlayer) continue;

        const pos = worldToCanvas(smoke.x, smoke.y, config, canvasSize);
        const r = worldRadius(SMOKE_RADIUS_WORLD, config, canvasSize);
        const smokeTeam = smokeTeams[smoke.entityId];
        const smokeRgb = smokeTeam === 'T' ? '200,180,130' : smokeTeam === 'CT' ? '130,160,200' : '200,200,200';

        const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, r);
        gradient.addColorStop(0, `rgba(${smokeRgb}, 0.55)`);
        gradient.addColorStop(0.7, `rgba(${smokeRgb}, 0.4)`);
        gradient.addColorStop(1, `rgba(${smokeRgb}, 0.1)`);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        const elapsed = frame.tick - smoke.startTick;
        const remaining = Math.max(0.05, 1 - elapsed / SMOKE_DURATION_TICKS);

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining);
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * remaining})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(200, 200, 200, 0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();

        const smokeIcon = options.preloadedIcons?.weapons[72];
        if (smokeIcon) {
            const iconSize = Math.max(r * 0.7, 14);
            const nw = smokeIcon.naturalWidth || smokeIcon.width || 10;
            const nh = smokeIcon.naturalHeight || smokeIcon.height || 10;
            const iconW = iconSize * (nw / nh);
            ctx.globalAlpha = 0.4 + remaining * 0.4;
            ctx.drawImage(smokeIcon, pos.x - iconW / 2, pos.y - iconSize / 2, iconW, iconSize);
            ctx.globalAlpha = 1;
        }
    }

    // --- Fires ---
    for (const fire of frame.world.fires) {
        const fireThrowerTeam = fireTeams[fire.uniqueId];
        if (fireThrowerTeam !== drawSide) continue;
        if (options.selectedPlayer && fireThrowers[fire.uniqueId] !== options.selectedPlayer) continue;

        const center = worldToCanvas(fire.x, fire.y, config, canvasSize);
        const fireTeam = fireTeams[fire.uniqueId];
        const teamRgb = fireTeam === 'T' ? '222,155,53' : fireTeam === 'CT' ? '93,121,174' : '255,100,20';

        const startFrame = fireStartFrames[fire.uniqueId];
        const elapsedFrames = startFrame != null ? frameIdx - startFrame : 0;
        const totalFrames = FIRE_DURATION_SECONDS * 64;
        const remaining = startFrame != null ? Math.max(0, 1 - elapsedFrames / totalFrames) : 1;

        if (fire.flames.length > 0) {
            const flameR = worldRadius(FLAME_RADIUS_WORLD, config, canvasSize);
            const alpha = remaining > 0 ? 0.7 + remaining * 0.3 : 0.7;
            const flameColor = fireTeam === 'T' ? `rgba(240,150,30,${alpha * 0.65})`
                : fireTeam === 'CT' ? `rgba(90,155,240,${alpha * 0.65})`
                : `rgba(240,120,20,${alpha * 0.65})`;
            ctx.fillStyle = flameColor;
            for (const flame of fire.flames) {
                const fp = worldToCanvas(flame.x, flame.y, config, canvasSize);
                ctx.beginPath();
                ctx.arc(fp.x, fp.y, flameR, 0, Math.PI * 2);
                ctx.fill();
            }
        } else {
            const r = worldRadius(FIRE_RADIUS_WORLD, config, canvasSize);
            const fireGrad = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, r);
            fireGrad.addColorStop(0, 'rgba(255, 80, 20, 0.5)');
            fireGrad.addColorStop(0.4, 'rgba(220, 50, 0, 0.35)');
            fireGrad.addColorStop(0.75, 'rgba(160, 30, 0, 0.15)');
            fireGrad.addColorStop(1, 'rgba(100, 20, 0, 0.02)');
            ctx.beginPath();
            ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
            ctx.fillStyle = fireGrad;
            ctx.fill();

            if (remaining > 0) {
                ctx.beginPath();
                ctx.arc(center.x, center.y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining);
                ctx.strokeStyle = `rgba(${teamRgb}, ${0.6 + remaining * 0.4})`;
                ctx.lineWidth = 2.5;
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(${teamRgb}, 0.3)`;
                ctx.lineWidth = 1;
                ctx.stroke();

                const fireIcon = options.preloadedIcons?.weapons[73];
                if (fireIcon) {
                    const iconSize = Math.max(r * 0.7, 14);
                    const fnw = fireIcon.naturalWidth || fireIcon.width || 10;
                    const fnh = fireIcon.naturalHeight || fireIcon.height || 10;
                    const iconW = iconSize * (fnw / fnh);
                    ctx.globalAlpha = 0.4 + remaining * 0.4;
                    ctx.drawImage(fireIcon, center.x - iconW / 2, center.y - iconSize / 2, iconW, iconSize);
                    ctx.globalAlpha = 1;
                }
            }
        }
    }

    // --- Flash/HE detonation bursts ---
    for (const det of detonations) {
        if (det.frameIdx > frameIdx || frameIdx - det.frameIdx > 60) continue;
        // Filter by team: only show detonations from the viewed team
        const throwerRosterIdx = det.throwerSteamId
            ? replay.roster.findIndex(r => r.steamId === det.throwerSteamId)
            : -1;
        if (throwerRosterIdx < 0 || replay.roster[throwerRosterIdx]?.team !== drawSide) continue;
        if (options.selectedPlayer && det.throwerSteamId !== options.selectedPlayer) continue;
        // Multi-level: gate by thrower Z so a detonation only draws on the correct
        // floor. Mirrors ReplayCanvas.tsx flashPops filtering.
        if (levelFilter) {
            if (det.throwerZ == null) continue;
            const z = det.throwerZ;
            const { level, zUpper, zLower } = levelFilter;
            const onLevel = z >= zUpper ? level === 'upper'
                : z <= zLower ? level === 'lower'
                : true;
            if (!onLevel) continue;
        }

        const pos = worldToCanvas(det.x, det.y, config, canvasSize);
        const age = (frameIdx - det.frameIdx) / 60;

        if (det.type === 'flash') {
            const radius = 15 + age * 50;
            const alpha = Math.max(0, 1 - age);

            ctx.beginPath();
            ctx.arc(pos.x, pos.y, Math.max(0, 12 - age * 12), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.fill();

            const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius);
            gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha * 0.8})`);
            gradient.addColorStop(0.3, `rgba(230, 240, 255, ${alpha * 0.5})`);
            gradient.addColorStop(0.7, `rgba(200, 220, 255, ${alpha * 0.2})`);
            gradient.addColorStop(1, 'rgba(180, 200, 255, 0)');
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius * 0.7, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
            ctx.lineWidth = 2;
            ctx.stroke();
        } else {
            const radius = 12 + age * 35;
            const alpha = Math.max(0, 0.9 - age * 0.9);

            ctx.beginPath();
            ctx.arc(pos.x, pos.y, Math.max(0, 8 - age * 8), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 220, 80, ${alpha})`;
            ctx.fill();

            const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius);
            gradient.addColorStop(0, `rgba(255, 160, 40, ${alpha * 0.7})`);
            gradient.addColorStop(0.4, `rgba(255, 80, 0, ${alpha * 0.4})`);
            gradient.addColorStop(1, 'rgba(180, 30, 0, 0)');
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();
        }
    }

    // --- Projectiles in flight with trajectory paths ---
    // Group surviving projectiles by slot+type and match each to its nearest existing
    // path so concurrent same-type grenades stay on separate trajectories.
    type ProjEntry = (typeof frame.world.projectiles)[number];
    type ProjPath = Array<{ x: number; y: number }>;
    const PROJ_PATH_MAX_JUMP = 600;

    const projsByKey = new Map<string, ProjEntry[]>();
    for (const proj of frame.world.projectiles) {
        if (proj.type === 1 && frame.world.smokes.some(s =>
            Math.abs(s.x - proj.x) < 50 && Math.abs(s.y - proj.y) < 50)) continue;
        if (proj.type === 4 && frame.world.fires.some(f =>
            Math.abs(f.x - proj.x) < 50 && Math.abs(f.y - proj.y) < 50)) continue;

        const throwerSlot = proj.throwerSlot !== 0xFF ? proj.throwerSlot : -1;
        if (throwerSlot < 0 || !isThrowerOnDrawSide(throwerSlot)) continue;
        if (options.selectedPlayer && replay.roster[throwerSlot]?.steamId !== options.selectedPlayer) continue;

        const key = `${throwerSlot}_${proj.type}`;
        const list = projsByKey.get(key);
        if (list) list.push(proj); else projsByKey.set(key, [proj]);
    }

    const renderQueue: Array<{ proj: ProjEntry; path: ProjPath }> = [];
    for (const [key, projs] of Array.from(projsByKey.entries())) {
        const paths = rs.projPaths.get(key) ?? [];
        const claimed = new Set<number>();
        const assigned: Array<ProjPath | null> = projs.map(() => null);

        while (true) {
            let bestProj = -1, bestPath = -1, bestDist = PROJ_PATH_MAX_JUMP;
            for (let pi = 0; pi < projs.length; pi++) {
                if (assigned[pi]) continue;
                const proj = projs[pi];
                for (let li = 0; li < paths.length; li++) {
                    if (claimed.has(li)) continue;
                    const tail = paths[li][paths[li].length - 1];
                    if (!tail) continue;
                    const d = Math.hypot(tail.x - proj.x, tail.y - proj.y);
                    if (d < bestDist) { bestDist = d; bestProj = pi; bestPath = li; }
                }
            }
            if (bestProj < 0) break;
            assigned[bestProj] = paths[bestPath];
            claimed.add(bestPath);
        }

        const survivingPaths: ProjPath[] = [];
        for (let li = 0; li < paths.length; li++) {
            if (claimed.has(li)) survivingPaths.push(paths[li]);
        }
        for (let pi = 0; pi < projs.length; pi++) {
            let path = assigned[pi];
            if (!path) {
                path = [];
                survivingPaths.push(path);
            }
            const proj = projs[pi];
            const tail = path[path.length - 1];
            if (!tail || Math.abs(tail.x - proj.x) > 2 || Math.abs(tail.y - proj.y) > 2) {
                path.push({ x: proj.x, y: proj.y });
            }
            renderQueue.push({ proj, path });
        }
        rs.projPaths.set(key, survivingPaths);
    }

    for (const key of Array.from(rs.projPaths.keys())) {
        if (!projsByKey.has(key)) rs.projPaths.delete(key);
    }

    for (const { proj, path } of renderQueue) {
        const throwerSlot = proj.throwerSlot;
        const throwerTeam = replay.roster[throwerSlot]?.team ?? null;
        const teamRgb = throwerTeam === 'T' ? '222,155,53' : throwerTeam === 'CT' ? '93,121,174' : '200,200,200';
        const pos = worldToCanvas(proj.x, proj.y, config, canvasSize);

        if (path.length >= 2) {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            const start = worldToCanvas(path[0].x, path[0].y, config, canvasSize);
            ctx.moveTo(start.x, start.y);
            for (let pi = 1; pi < path.length; pi++) {
                const pt = worldToCanvas(path[pi].x, path[pi].y, config, canvasSize);
                ctx.lineTo(pt.x, pt.y);
            }
            ctx.strokeStyle = `rgba(${teamRgb}, 0.7)`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.setLineDash([]);
        }

        const weaponId = PROJ_TO_WEAPON[proj.type];
        const projImg = weaponId != null ? options.preloadedIcons?.weapons[weaponId] : null;

        if (projImg) {
            const h = 12;
            const nw = projImg.naturalWidth || projImg.width || 10;
            const nh = projImg.naturalHeight || projImg.height || 10;
            const w = h * (nw / nh);
            ctx.fillStyle = `rgba(${teamRgb}, 0.6)`;
            ctx.beginPath();
            ctx.roundRect(pos.x - w / 2 - 2, pos.y - h / 2 - 2, w + 4, h + 4, 3);
            ctx.fill();
            ctx.drawImage(projImg, pos.x - w / 2, pos.y - h / 2, w, h);
        } else {
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, 3.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${teamRgb}, 0.8)`;
            ctx.fill();
        }
    }
}

export function renderRoundPlayers(
    ctx: CanvasRenderingContext2D,
    frame: Frame,
    roster: RosterEntry[],
    drawSide: 'T' | 'CT',
    mapName: string,
    canvasSize: number,
    options: RenderOptions,
    isHovered: boolean = false,
    playerNames: Record<string, string> = {},
) {
    const config = mapConfigs[mapName];
    if (!config) return;

    let teamSlotIdx = 0;
    // When a pinned player exists on this team side, reserve color 0 for them and
    // start the regular slot counter at 1 so colors don't collide.
    const pinned = options.pinnedPlayerSteamId;
    const pinnedOnThisSide = pinned
        ? roster.some(r => r && r.team === drawSide && r.steamId === pinned)
        : false;
    if (pinnedOnThisSide) teamSlotIdx = 1;

    for (let i = 0; i < roster.length; i++) {
        const entry = roster[i];
        if (!entry || entry.team !== drawSide) continue;

        const isPinned = pinnedOnThisSide && entry.steamId === pinned;

        if (options.selectedPlayer && entry.steamId !== options.selectedPlayer) {
            if (!isPinned) teamSlotIdx++;
            continue;
        }

        const player = frame.players?.[i];
        if (!player) { if (!isPinned) teamSlotIdx++; continue; }

        const pos = worldToCanvas(player.x, player.y, config, canvasSize);
        if (player.x === 0 && player.y === 0) { if (!isPinned) teamSlotIdx++; continue; }

        const colorIdx = isPinned ? 0 : (teamSlotIdx % options.playerColors.length);

        if (!player.isAlive) {
            // Dead player X marker
            ctx.globalAlpha = 0.3;
            ctx.strokeStyle = options.playerColors[colorIdx];
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(pos.x - 4, pos.y - 4);
            ctx.lineTo(pos.x + 4, pos.y + 4);
            ctx.moveTo(pos.x + 4, pos.y - 4);
            ctx.lineTo(pos.x - 4, pos.y + 4);
            ctx.stroke();
            ctx.globalAlpha = 1;
            if (!isPinned) teamSlotIdx++;
            continue;
        }

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = options.playerColors[colorIdx];
        ctx.fill();
        ctx.strokeStyle = isHovered ? '#2ecc71' : 'rgba(0,0,0,0.5)';
        ctx.lineWidth = isHovered ? 2.5 : 1;
        ctx.stroke();

        // Name label when hovered
        if (isHovered) {
            const name = playerNames[entry.steamId] || entry.steamId.slice(-6);
            ctx.font = 'bold 10px sans-serif';
            const tw = ctx.measureText(name).width;
            const labelX = pos.x - tw / 2 - 3;
            const labelY = pos.y - 14;
            ctx.fillStyle = 'rgba(50, 50, 50, 0.85)';
            ctx.beginPath();
            ctx.roundRect(labelX, labelY - 9, tw + 6, 14, 3);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(name, pos.x, labelY - 2);
        }

        if (!isPinned) teamSlotIdx++;
    }
}
